import {Component, h} from "preact";
import styles from './style.module.scss';
import {connect} from "redux-zero/preact";
import actions from "../../../store/actions";
import {productService} from "../../../services/product-service";
import {Image, Product, Variant} from "../../../models/product";
import ProductImage from "../../product-image";
import ProductInfoDesktop from "./product-info";
import {ShootingSize} from "../../shooting-size";
import Navigation from "./navigation";
import Colors from "../../colors";
import Button from "../../button";
import {i18nService} from "../../../services/i18n-service";
import MatchingProducts from "../../matching-products";
import {icons} from "../../icons";
import {customerService} from "../../../services/customer-service";
import {Like} from "../../../models/customer";
import {route} from "preact-router";
import ImagePreview from "../../image-preview";
import {Dimension, NY_Tracker} from "../../../services/tracking-service";
import App from "../../app";
import {NY_CountryService} from "../../../services/country-service";
import {Epr, EprOpenedTrackingEvent} from "../../epr";

interface Props {
  toggleLike(variant: Variant, newLikeState: boolean, dimension: string);

  product: Product;
  variant: Variant;
  likes: Like[];
}

interface State {
  isLiked: boolean;
  fullscreen: { show: boolean, start: Image | null };
  isSizeInfoExpanded: boolean;
  isMaterialExpanded: boolean;
  isDisposalHintExpanded: boolean;
  showEpr: boolean;
}

const mapToProps = ({likes}) => ({likes});

enum ToggleableKey {
  isMaterialExpanded = 'isMaterialExpanded',
  isDisposalHintExpanded = 'isDisposalHintExpanded',
  isSizeInfoExpanded = 'isSizeInfoExpanded'
}

export const MetallicFibreWebCategory = ["WCA01152", "WCA03012", "WCA03080", "WCA03079", "WCA03013", "WCA03011", "WCA01157"]

class DetailDesktop extends Component<Props, State> {

  constructor() {
    super();
    this.showFullscreen = this.showFullscreen.bind(this);
    this.hideFullscreen = this.hideFullscreen.bind(this);
    this.showEpr = this.showEpr.bind(this);
    this.hideEpr = this.hideEpr.bind(this);
    this.handleEprKeyPress = this.handleEprKeyPress.bind(this);
    this.handleMaterialKeyPress = this.handleMaterialKeyPress.bind(this);
    this.handleDisposalHintKeyPress = this.handleDisposalHintKeyPress.bind(this);
  }

  state: State = {
    isLiked: false,
    fullscreen: {show: false, start: null},
    isSizeInfoExpanded: false,
    isMaterialExpanded: false,
    isDisposalHintExpanded: false,
    showEpr: false
  };

  private toggle(key: ToggleableKey) {
    this.setState((prevState) => ({
      [key]: !prevState[key],
    }) as Partial<State>)
  }

  componentDidMount(): void {
    this.updateLikeState();
  }

  componentDidUpdate(previousProps: Readonly<Props>, previousState: Readonly<State>, previousContext: any): void {
    if (this.props.product && this.props.variant) {
      if (this.state.isLiked !== customerService.isVariantLiked(this.props.variant, this.props.likes)) {
        this.updateLikeState();
      }

      if (previousProps.product != this.props.product || previousProps.variant != this.props.variant) {
        this.setState({
          isMaterialExpanded: false,
          isDisposalHintExpanded: false,
          isSizeInfoExpanded: false,
          showEpr: false
        })
      }
    }
  }

  private updateLikeState() {
    if (this.props.variant) {
      this.setState({isLiked: customerService.isVariantLiked(this.props.variant, this.props.likes)})
    }
  }

  private showFullscreen(image: Image) {
    this.setState({fullscreen: {show: true, start: image}});
  }

  private hideFullscreen() {
    this.setState({fullscreen: {show: false, start: null}})
  }

  private showEpr() {
    this.setState({showEpr: true});
    NY_Tracker.trackClick(EprOpenedTrackingEvent);
  }

  private hideEpr() {
    this.setState({showEpr: false});
  }

  private handleMaterialKeyPress(e: KeyboardEvent) {
    if (e.key == "Enter") {
      this.toggle(ToggleableKey.isMaterialExpanded);
    }
  }

  private handleDisposalHintKeyPress(e: KeyboardEvent) {
    if(e.key == "Enter") {
      this.toggle(ToggleableKey.isDisposalHintExpanded);
    }
  }

  private handleSizeInfoKeyPress(e: KeyboardEvent) {
    if(e.key == "Enter") {
      this.toggle(ToggleableKey.isSizeInfoExpanded);
    }
  }

  private handleEprKeyPress(e: KeyboardEvent) {
    if (e.key == "Enter") {
      this.showEpr();
    }
  }

  render() {
    if (!(this.props.product && this.props.variant)) {
      return (
        <div class={styles['product-not-found-container']}>
          <div className={styles['header-container']}>
            <Navigation product={this.props.product} variant={this.props.variant}/>
          </div>
          <div className={styles['no-products']}>
            <div className={styles['info-text']}>{i18nService.translate('detail_view_product_not_found')}</div>
            <div className={styles['filter-reset']}>
              <Button black={true} label={i18nService.translate('detail_view_back_to_products_button')}
                      onClickCallback={() => route('/products/')}/>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div class={styles['detail-container']}>
        <div class={styles['header-container']}>
          <Navigation product={this.props.product} variant={this.props.variant}/>
        </div>
        <div class={styles['product-container']}>
          <div class={styles['images-container']}>
            {this.props.variant.images.map((image => {
              return (
                <div class={`${styles['image']}`}>
                  <ProductImage onClickCallback={(image: Image) => this.showFullscreen(image)}
                                image={image}
                                alt={`${i18nService.translate(productService.getTranslationKey(this.props.product))} | ${this.props.variant.getColorTranslation()} | NEW YORKER`}
                                title={`${i18nService.translate(productService.getTranslationKey(this.props.product))} | ${this.props.variant.getColorTranslation()} | NEW YORKER`}
                                resolution={image.type === 'CUTOUT' ? 'mid' : 'full-hd'}/>
                </div>)
            }))
            }
          </div>
          {this.state.fullscreen.show ?
            <ImagePreview onEscapeCallback={this.hideFullscreen} onClickCallback={this.hideFullscreen}
                          image={this.state.fullscreen.start}
                          imageAlt={`${i18nService.translate(productService.getTranslationKey(this.props.product))} | ${this.props.variant.getColorTranslation()} | NEW YORKER`}/> : null}
          <div class={styles['info-container']}>
            <div className={styles['info-sticky']}>
              <ProductInfoDesktop product={this.props.product} variant={this.props.variant}/>
              <div className={styles['separator']}/>
              <Colors product={this.props.product} selected={this.props.variant}/>

              <div className={styles['separator']}/>

              {!NY_CountryService.isRestricted(App.countryCode) && <div className={styles.like}>
                <Button svg={icons.renderHeartIcon()}
                        label={i18nService.translate(this.state.isLiked ? 'detail_view_dislike_button' : 'detail_view_like_button')}
                        onClickCallback={this.handleToggleLike.bind(this)}
                        black={this.state.isLiked}
                        white={!this.state.isLiked}/>
              </div>}

              <div className={styles['separator']}/>

              {(this.props.variant.components.length > 0 || NY_CountryService.getCountryCode() == "fr") && <div className={styles['additional-container']}>
                <div
                    className={styles['additional-title']}
                    onClick={() => this.toggle(ToggleableKey.isMaterialExpanded)}
                    onKeyPress={this.handleMaterialKeyPress}
                    tabindex={0}
                    aria-label={i18nService.translate("detail_view_product_information")}
                >
                  <span>{i18nService.translate("detail_view_product_information")}</span>

                  <span className={styles['toggle-arrow']}>
                    {this.state.isMaterialExpanded ? icons.renderChevronUpIcon() : icons.renderChevronDownIcon()}
                  </span>
                </div>

                {this.state.isMaterialExpanded && (
                  <>
                    {this.props.variant.components.length > 0 && (
                      <div className={styles['details']}>
                        {this.props.variant.components.map((component) => (
                            <div key={component.id}>
                              <span
                                  className={styles['label']}>{i18nService.translate(component.translationKey())}: </span>
                              <span>{component.materials.map(material => `${material.value}% ${i18nService.translate(material.translationKey())}`).join(", ")}</span>
                            </div>
                        ))}
                      </div>
                    )}

                    {NY_CountryService.getCountryCode() == "fr" && (
                      <>
                        <div className={styles["epr"]}>
                          {this.props.variant.hasAllCountryData() ? (
                            <span
                              onClick={this.showEpr}
                              tabIndex={0}
                              onKeyPress={this.handleEprKeyPress}
                              aria-label={i18nService.translate("detail_view_epr_label")}
                            >
                            {i18nService.translate("detail_view_epr_label")}
                          </span>
                          ) : (
                            <a
                              href={"/legal/Extended_Producer_Responsibility_FR.pdf"}
                              target={"_blank"}
                              aria-label={i18nService.translate("detail_view_epr_label")}
                            >
                              {i18nService.translate("detail_view_epr_label")}
                            </a>
                          )}
                        </div>

                        {this.props.variant.components.some(c => c.hasMetallicFibre()) && !MetallicFibreWebCategory.includes(this.props.product.web_category) && (
                          <div className={styles["metallic-fibre"]}>
                            {icons.renderInfoIcon()}
                            <span>{i18nService.translate("detail_view_metallic_fibre")}</span>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>}


              {(this.props.product.web_category == 'WCA04009' || this.props.product.web_category == 'WCA04010') && // M_Uhren || W_Uhren
                <>
                  <div className={styles['separator']}/>

                  <div className={`${styles['additional-container']} ${styles['disposal-hint']}`}>
                    <div
                      className={styles['additional-title']}
                      onClick={() => this.toggle(ToggleableKey.isDisposalHintExpanded)}
                      onKeyPress={this.handleDisposalHintKeyPress}
                      tabindex={0}
                      aria-label={i18nService.translate("detail_view_disposal_instructions_title")}>

                      <span>{i18nService.translate("detail_view_disposal_instructions_title")}</span>
                      <span className={styles['icon']}>{icons.renderCrossedOutWheeledBinIcon()}</span>
                      <span className={styles['toggle-arrow']}>
                        {this.state.isDisposalHintExpanded ? icons.renderChevronUpIcon() : icons.renderChevronDownIcon()}
                      </span>
                    </div>

                    {this.state.isDisposalHintExpanded && (
                      <>
                        <div className={styles['details']}>
                          {i18nService.translate("detail_view_disposal_instructions_text")}
                        </div>
                      </>
                    )}
                  </div>
                </>}
              <div className={styles['separator']}/>
              <ShootingSize variant={this.props.variant}/>
            </div>
          </div>
        </div>
        <div class={styles['matching-products-container']}>
          <MatchingProducts productId={this.props.variant.productId} variantId={this.props.variant.id}/>
        </div>

        {NY_CountryService.getCountryCode() == "fr" && <Epr
            show={this.state.showEpr}
            onClose={this.hideEpr}
            variant={this.props.variant}
        />}
      </div>
    )
  }

  async handleToggleLike(e: Event) {
    e.stopPropagation();

    await this.props.toggleLike(this.props.variant, !this.state.isLiked, Dimension.DETAIL);

    this.updateLikeState();
  }
}

export default connect(mapToProps, actions)(DetailDesktop)
